import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ClipboardList, AlertTriangle, PauseCircle, Filter, Plus } from 'lucide-react'
import api from '@/lib/api'

type Rdc = {
  id: string
  code: string
  status: string
  tipo_comercial: string
  tipo_tecnico: string | null
  descricao_cliente: string
  opportunity_id: string
  suspenso: boolean
  pode_ofertar: boolean
  bloqueio_oferta: string | null
  frase_para_cliente: string
  prazo_agenda_em: string | null
  prazo_conclusivo_em: string | null
  created_at: string
}

// Rotulos e cor por estado. A cor encoda urgencia, nao decoracao:
// cinza = em coleta, ambar = aguardando alguem, verde = liberado, vermelho = travado.
const ESTADOS: Record<string, { label: string; cls: string }> = {
  coleta: { label: 'Em coleta', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  registrado: { label: 'Aguardando agenda', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  passagem_agendada: { label: 'Passagem agendada', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  acordo_pendente: { label: 'De acordo pendente', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  de_acordo: { label: 'De acordo', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  parecer_preliminar: { label: 'Parecer preliminar', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  parecer_conclusivo: { label: 'Parecer conclusivo', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  financeiro_pendente: { label: 'Aguardando Financeiro', cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
  habilitado: { label: 'Habilitado para oferta', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  deliberacao: { label: 'Em deliberação', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' },
  encerrado: { label: 'Encerrado', cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' },
}

const TIPOS: Record<string, string> = {
  A: 'A · Escopo padrão',
  B: 'B · Não confirmada',
  C: 'C · Nova funcionalidade',
  D: 'D · Projeto aderente',
  E: 'E · Sem aderência',
}

const dt = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—'

export default function RdcPage() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const [itens, setItens] = useState<Rdc[]>([])
  const [carregando, setCarregando] = useState(true)
  const [visao, setVisao] = useState<'todos' | 'atrasados' | 'meus'>('todos')
  const [medicao, setMedicao] = useState<any>(null)

  const carregar = useCallback(() => {
    const q: Record<string, any> = { per_page: 200 }
    if (visao === 'atrasados') q.atrasados = true
    setCarregando(true)
    api.get('/v1/rdc/', { params: q })
      .then(({ data }) => setItens(data.items || []))
      .finally(() => setCarregando(false))
  }, [visao])

  useEffect(() => { carregar() }, [carregar])

  // Compatibilidade: os e-mails ja enviados apontam para /rdc?rdc=<id>. Hoje cada
  // registro tem tela propria, entao a querystring apenas redireciona.
  useEffect(() => {
    const abrir = params.get('rdc')
    if (abrir) nav(`/rdc/${abrir}`, { replace: true })
  }, [params, nav])

  const carregarMedicao = useCallback(() => {
    api.get('/v1/rdc/painel/medicao')
      .then(({ data }) => setMedicao(data))
      .catch(() => setMedicao(null))   // 403 para quem não é diretoria: o painel some
  }, [])

  useEffect(() => { carregarMedicao() }, [carregarMedicao])

  const atrasados = useMemo(
    () => itens.filter((r) => {
      if (r.suspenso || ['habilitado', 'encerrado'].includes(r.status)) return false
      const p = r.prazo_conclusivo_em || r.prazo_agenda_em
      return p ? new Date(p) < new Date() : false
    }).length,
    [itens],
  )

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardList className="w-6 h-6" /> Registro de Demanda Comercial
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Demandas identificadas junto a clientes e prospects, na forma do Procedimento
            Comercial × Tecnologia. Demanda que não estiver aqui não existe para efeito do
            procedimento, e o time técnico não é cobrável por ela.
          </p>
        </div>
        <button
          onClick={() => nav('/rdc/novo')}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm
                     font-medium text-primary-foreground hover:opacity-90 shrink-0"
        >
          <Plus className="w-4 h-4" /> Novo RDC
        </button>
      </div>

      {medicao && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          {[
            ['Total', medicao.total, ''],
            ['No prazo', medicao.no_prazo, 'text-emerald-600'],
            ['Fora do prazo', medicao.fora_do_prazo, 'text-rose-600'],
            ['Suspensos', medicao.suspensos, 'text-amber-600'],
          ].map(([rot, val, cor]: any) => (
            <div key={rot} className="rounded-lg border bg-card p-4">
              <div className={`text-2xl font-semibold tabular-nums ${cor}`}>{val}</div>
              <div className="text-xs text-muted-foreground mt-1">{rot}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {([['todos', 'Todos'], ['atrasados', `Vencidos (${atrasados})`]] as const).map(([k, rot]) => (
          <button
            key={k}
            onClick={() => setVisao(k as any)}
            className={`px-3 py-1.5 rounded-md text-sm border transition ${
              visao === k ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-muted'
            }`}
          >
            {rot}
          </button>
        ))}
      </div>

      <div className="rounded-lg border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              {['RDC', 'Demanda', 'Tipo', 'Estado', 'Agenda', 'Conclusivo', 'Oferta'].map((h) => (
                <th key={h} className="text-left font-medium text-xs uppercase tracking-wide
                                       text-muted-foreground px-4 py-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {carregando && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Carregando…</td></tr>
            )}
            {!carregando && itens.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                Nenhum RDC neste filtro. Use “Novo RDC”, ou abra um a partir de uma oportunidade.
              </td></tr>
            )}
            {itens.map((r) => {
              const e = ESTADOS[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-700' }
              return (
                <tr key={r.id}
                    onClick={() => nav(`/rdc/${r.id}`)}
                    className="border-b last:border-0 hover:bg-muted/40 cursor-pointer">
                  <td className="px-4 py-3 font-medium whitespace-nowrap">
                    {r.code}
                    {r.suspenso && (
                      <PauseCircle className="inline w-3.5 h-3.5 ml-1.5 text-amber-600" aria-label="prazo suspenso" />
                    )}
                  </td>
                  <td className="px-4 py-3 max-w-md">
                    <div className="line-clamp-2 text-muted-foreground">{r.descricao_cliente}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs">
                    {TIPOS[r.tipo_tecnico || r.tipo_comercial]}
                    {r.tipo_tecnico && r.tipo_tecnico !== r.tipo_comercial && (
                      <span className="block text-[11px] text-amber-600">reclassificado de {r.tipo_comercial}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block whitespace-nowrap px-2 py-0.5 rounded-full
                                      text-xs font-medium ${e.cls}`}>
                      {e.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 tabular-nums whitespace-nowrap">{dt(r.prazo_agenda_em)}</td>
                  <td className="px-4 py-3 tabular-nums whitespace-nowrap">{dt(r.prazo_conclusivo_em)}</td>
                  <td className="px-4 py-3">
                    {r.pode_ofertar ? (
                      <span className="text-emerald-600 text-xs font-medium">liberada</span>
                    ) : (
                      <span className="text-xs text-muted-foreground flex items-start gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
                        <span className="line-clamp-2">{r.bloqueio_oferta}</span>
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
